import type { AnySceneObjectT, SimpleStyleT } from "tweenly"
import { UpdateObjectsDifferentStyles } from "../store/slices/objects.slice/object/objectTypes"
import { getObjectPositioning } from "./object.helpers"
import { affineMemberScale, affineMemberStretch, ownLinearTransform } from "./affineResize.helpers"
import type { SpaceT } from "./domSpace.helpers"
import { fromSpace, toSpace } from "./domSpace.helpers"
import {
    applyTransform,
    LinearTransform,
    multiplyTransforms,
    Point,
    toLocalDelta,
    transformOriginPoint,
} from "./orientedResize.helpers"
import { ObjectStylesWithId, ObjectsById } from "./wrapper.helpers"

/**
 * Resizing a multiselection, the way Figma does it.
 *
 * A multiselection is treated as ONE bounding box: dragging a handle changes that box, and
 * everything inside is scaled by the same ratio against that shared boundary rather than
 * against its own edges. So an object sitting a third of the way across the selection stays
 * a third of the way across it, and the gaps between objects grow with the box.
 *
 *     x' = L' + (x − L) · sx        w' = w · sx
 *     y' = T' + (y − T) · sy        h' = h · sy
 *
 * Deliberately NOT scaled: font size, stroke width, corner radius. Figma keeps those on a
 * normal resize too — scaling them is what its separate Scale tool (K) is for.
 *
 * Pure: no DOM, no Redux, no React. The caller supplies the geometry and decides what to do
 * with the returned style updates.
 */

export type ScaleBox = {
    top: number
    left: number
    width: number
    height: number
    isMirroredX?: boolean
    isMirroredY?: boolean
}

/**
 * A selection may never be scaled to nothing. Every size would be multiplied by zero, and
 * because zero stays zero however far the handle is dragged back out, the objects could
 * never be recovered by continuing the same gesture.
 */
const MIN_BOX_SIZE = 1

/**
 * Carry `box` along when the box drawn around the selection goes from `drawnBefore` to
 * `drawnAfter` — the same scale about the same edge, mirrored the same way.
 *
 * The selection is DRAWN around what is on screen (rotation, borders, every transform
 * included), while the resize maths runs on the objects' styles. The two boxes differ, so
 * the drag on the drawn one is replayed on the style one: every point keeps its relative
 * place, which is what the preview of the drawn box showed.
 */
export const mapBoxThroughResize = (
    box: ScaleBox,
    drawnBefore: ScaleBox,
    drawnAfter: ScaleBox
): ScaleBox => {
    const nextWidth = Math.max(drawnAfter.width, MIN_BOX_SIZE)
    const nextHeight = Math.max(drawnAfter.height, MIN_BOX_SIZE)
    const scaleX = drawnBefore.width > 0 ? nextWidth / drawnBefore.width : 1
    const scaleY = drawnBefore.height > 0 ? nextHeight / drawnBefore.height : 1

    // measured from the drawn box's left / top edge — or its right / bottom one once mirrored
    const left = drawnAfter.isMirroredX
        ? drawnAfter.left + nextWidth - (box.left + box.width - drawnBefore.left) * scaleX
        : drawnAfter.left + (box.left - drawnBefore.left) * scaleX
    const top = drawnAfter.isMirroredY
        ? drawnAfter.top + nextHeight - (box.top + box.height - drawnBefore.top) * scaleY
        : drawnAfter.top + (box.top - drawnBefore.top) * scaleY

    return {
        left,
        top,
        width: box.width * scaleX,
        height: box.height * scaleY,
        isMirroredX: drawnAfter.isMirroredX,
        isMirroredY: drawnAfter.isMirroredY,
    }
}

/**
 * The new width/height STYLE for an object drawn `drawn` px wide, of which `current` is the
 * style and the rest border + padding (content-box).
 *
 * The drawn box is what scales with the selection; the border and padding stay as thick as
 * they were, the way Figma keeps stroke widths. Scaling the style alone would leave every
 * bordered member short of the box it was drawn into.
 *
 * A non-empty object never rounds down to nothing. Clamping the selection box alone is not
 * enough: an object rounded to 0 stays at 0 no matter how far the handle is dragged back
 * out, because the ratio multiplies it. Squashing a selection flat and pulling it open again
 * would leave every object gone for good.
 */
const scaleStyleSize = (current: number, drawn: number, ratio: number): number => {
    const extra = Math.max(0, drawn - current)
    return current > 0 ? Math.max(1, Math.round(drawn * ratio - extra)) : 0
}

/**
 * Where a box of the given size is drawn around — its centre on the canvas — once drawn
 * through `turn` about its transform origin, relative to its untransformed top-left.
 */
const drawnCentreOffset = (
    turn: LinearTransform,
    origin: unknown,
    width: number,
    height: number
) => {
    const o = transformOriginPoint(origin, width, height)
    const r = applyTransform(turn, { x: width / 2 - o.x, y: height / 2 - o.y })
    return { x: o.x + r.x, y: o.y + r.y }
}

/**
 * The resize as the selection is DRAWN — before and after, in absolute canvas units — and the
 * measured space (domSpace.helpers, in the same units) of the parent of every member whose
 * parent is not scaled with it. Such a member is resized in the real geometry of the canvas,
 * not in the styles' summed-up one, which knows nothing about a turned, scaled or animated
 * parent: its centre keeps its place in the drawn box, exactly like the outline.
 */
export type DrawnResizeT = {
    before: ScaleBox
    after: ScaleBox
    parentSpaces: Record<string, SpaceT>
}

/**
 * Where a point of the drawn box goes when the box is resized — the same scale about the same
 * edge, mirrored the same way (see mapBoxThroughResize).
 */
const mapPointThroughResize = (point: Point, before: ScaleBox, after: ScaleBox): Point => {
    const nextWidth = Math.max(after.width, MIN_BOX_SIZE)
    const nextHeight = Math.max(after.height, MIN_BOX_SIZE)
    const x = (point.x - before.left) * (before.width > 0 ? nextWidth / before.width : 1)
    const y = (point.y - before.top) * (before.height > 0 ? nextHeight / before.height : 1)
    return {
        x: after.left + (after.isMirroredX ? nextWidth - x : x),
        y: after.top + (after.isMirroredY ? nextHeight - y : y),
    }
}

/**
 * The selection's scale along the canvas, (scaleX, scaleY), as seen in a space with the given
 * axes: P⁻¹·S·P. Diagonal again for a parent that is only scaled or flipped.
 */
const localStretch = (axes: LinearTransform, scaleX: number, scaleY: number): LinearTransform => {
    const scaled = multiplyTransforms({ a: scaleX, b: 0, c: 0, d: scaleY }, axes)
    const column = (x: number, y: number) => toLocalDelta(axes, { x, y })
    const first = column(scaled.a, scaled.b)
    const second = column(scaled.c, scaled.d)
    return { a: first.x, b: first.y, c: second.x, d: second.y }
}

/** Replace the numeric styles an object already has. Missing properties are not added. */
const withUpdatedStyles = (
    object: AnySceneObjectT,
    changes: Record<string, number>
): SimpleStyleT[] =>
    object.styles.map((style) => {
        const next = changes[String(style.property)]
        return next === undefined ? style : { ...style, value: next }
    })

/**
 * @param objectsAbsolute every object in the selection subtree, in absolute canvas
 * coordinates — exactly what `computeWrapperBounds` returns
 * @param before the selection's bounding box before the resize
 * @param after the same box after it, in the same coordinate space
 * @param drawn the same resize as drawn, for the members inside a parent that is not scaled
 * @returns one style update per object that actually moved or changed size, and one
 *          transforms update per turned object that had to be sheared to fit
 */
export const scaleObjectsInBounds = (
    objectsAbsolute: ObjectStylesWithId[],
    before: ScaleBox,
    after: ScaleBox,
    objectsById: ObjectsById,
    drawn?: DrawnResizeT
): UpdateObjectsDifferentStyles[] => {
    const nextWidth = Math.max(after.width, MIN_BOX_SIZE)
    const nextHeight = Math.max(after.height, MIN_BOX_SIZE)
    const scaleX = before.width > 0 ? nextWidth / before.width : 1
    const scaleY = before.height > 0 ? nextHeight / before.height : 1

    const scalingIds = new Set(objectsAbsolute.map((entry) => entry.objectId))
    const updates: UpdateObjectsDifferentStyles[] = []
    /**
     * How much each object's own box grew. A child of a scaled object lives in that object's
     * own frame, which grew by exactly this — not by the selection's scale once the parent
     * is turned. (`objectsAbsolute` lists every parent before its children.)
     */
    const ratios = new Map<string, { x: number; y: number }>()

    objectsAbsolute.forEach((entry) => {
        const object = objectsById[entry.objectId]
        if (!object) return

        const styles = getObjectPositioning(object)
        const currentLeft = Number(styles.left) || 0
        const currentTop = Number(styles.top) || 0

        /**
         * top/left are stored relative to the parent, so the answer depends on whether the
         * parent is being scaled as well.
         *
         * If it is, the parent's own move already carries this object along and only its
         * offset INSIDE the parent has to grow. If it is not, the parent stays put and this
         * object has to absorb the whole move, measured against that unchanged parent —
         * `entry.left − currentLeft` is precisely the parent's absolute position.
         */
        const parentIsScaling = Boolean(object.parentId && scalingIds.has(object.parentId))

        /**
         * The RESOLVED size, not the style.
         *
         * `styles.width` is the string `"auto"` for a self-sizing object (text), and
         * `Number("auto") || 0` is 0 — while `entry.left`, which the offset below is derived
         * from, was measured by `domObjectSize` and does know the real width. Mixing the two
         * put every `auto`-sized member a full width away from where it belongs as soon as
         * the selection was mirrored.
         */
        const frameScale = (parentIsScaling && ratios.get(object.parentId!)) || {
            x: scaleX,
            y: scaleY,
        }
        /**
         * The parent's measured space, when the member is placed in a parent that stays as it
         * is. The selection's scale along the canvas then reaches the member, in that
         * parent's axes, as P⁻¹·S·P.
         */
        const parentSpace = parentIsScaling ? undefined : drawn?.parentSpaces[object.id]
        const stretch = parentSpace && localStretch(parentSpace.axes, scaleX, scaleY)
        // a turned member is stretched exactly like the selection (see affineMemberScale);
        // one that is not simply takes the two scales
        const affine = stretch
            ? affineMemberStretch(object, stretch)
            : affineMemberScale(object, frameScale.x, frameScale.y)
        const ratioX = affine?.ratioX ?? stretch?.a ?? frameScale.x
        const ratioY = affine?.ratioY ?? stretch?.d ?? frameScale.y
        ratios.set(object.id, { x: ratioX, y: ratioY })
        const scaledWidth = entry.width * ratioX
        const scaledHeight = entry.height * ratioY

        /**
         * Offset of this object from the box origin, after scaling.
         *
         * When the box is mirrored the members swap sides: what sat `d` from the left now
         * sits `d` from the right, so its own (scaled) width has to come off as well or the
         * object would hang past the edge.
         *
         * Only the members themselves are rearranged. A child inside one of them keeps its
         * place within its parent, the same way a single object's own content is not
         * mirrored either — what flips is the LAYOUT of the selection, never the insides of
         * anything in it.
         */
        /**
         * It is the point the member is DRAWN around — its centre once turned — that keeps
         * its relative place in the selection; the top-left is then wherever the resized,
         * turned box needs it to be. Unturned, that is the same as scaling the offset of the
         * top-left, which is what this used to do.
         */
        const own = ownLinearTransform(object.transforms)
        const origin = object.styles.find((style) => style.property === "transformOrigin")?.value
        const centreBefore = drawnCentreOffset(own, origin, entry.width, entry.height)
        const centreAfter = drawnCentreOffset(
            affine?.linear ?? own,
            origin,
            scaledWidth,
            scaledHeight
        )
        const centreX = (entry.left + centreBefore.x - before.left) * scaleX
        const centreY = (entry.top + centreBefore.y - before.top) * scaleY
        const mirroredCentreX = after.isMirroredX ? nextWidth - centreX : centreX
        const mirroredCentreY = after.isMirroredY ? nextHeight - centreY : centreY

        // in the real geometry: the centre keeps its place in the drawn box, and is placed
        // back into the parent's own axes
        const realCentre =
            parentSpace &&
            drawn &&
            toSpace(
                parentSpace,
                mapPointThroughResize(
                    fromSpace(parentSpace, {
                        x: currentLeft + centreBefore.x,
                        y: currentTop + centreBefore.y,
                    }),
                    drawn.before,
                    drawn.after
                )
            )

        // inside a scaled parent, the centre keeps its place in the parent's grown frame
        const nextLeft = realCentre
            ? realCentre.x - centreAfter.x
            : parentIsScaling
            ? (currentLeft + centreBefore.x) * frameScale.x - centreAfter.x
            : after.left + mirroredCentreX - centreAfter.x - (entry.left - currentLeft)
        const nextTop = realCentre
            ? realCentre.y - centreAfter.y
            : parentIsScaling
            ? (currentTop + centreBefore.y) * frameScale.y - centreAfter.y
            : after.top + mirroredCentreY - centreAfter.y - (entry.top - currentTop)

        const changes: Record<string, number> = {}

        // A flex child is placed by its parent's layout, so its top/left mean nothing and
        // the absolute position derived from them is fiction. Its SIZE still scales.
        if (styles.position !== "static") {
            changes.left = Math.round(nextLeft)
            changes.top = Math.round(nextTop)
        }

        // `auto` means the object sizes itself (text). Writing a number would freeze that
        // for good, so those objects are repositioned but keep their own dimensions.
        if (styles.width !== "auto") {
            changes.width = scaleStyleSize(Number(styles.width) || 0, entry.width, ratioX)
        }
        if (styles.height !== "auto") {
            changes.height = scaleStyleSize(Number(styles.height) || 0, entry.height, ratioY)
        }

        const unchanged = Object.keys(changes).every(
            (property) =>
                changes[property] === (Number(styles[property as keyof typeof styles]) || 0)
        )
        if (!unchanged) {
            updates.push({
                object,
                property: "styles",
                value: withUpdatedStyles(object, changes),
            })
        }
        if (affine?.transforms) {
            updates.push({ object, property: "transforms", value: affine.transforms })
        }
    })

    return updates
}
